<template>
    <div id="dashboard-profile-view">
        <span class="dashboard-title mb-5">Account Overview</span>

        <div class="dashboard-boxed-container-wrapper d-flex mb-4">
            <div class="dashboard-boxed-container mr-4">
                <span class="dashboard-subtitle mb-4">Profile</span>
                <v-row>
                    <v-col sm="3" class="d-flex align-center">
                        Name
                    </v-col>
                    <v-col sm="9" class="d-flex">
                        <v-text-field
                            v-model="form.name"
                            class="color-gray d-block inline-edit-input"
                            dark
                            hide-details
                            :disabled="!editingName"
                            @blur="endEditing('name')"
                            @keydown="updateProfileHandler($event, 'name')"
                            ref="name"
                        />

                        <v-icon v-if="!editingName" @click="startEditing('name')" class="color-gray ml-2">mdi
                            mdi-pencil
                        </v-icon>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col sm="3" class="d-flex align-center">
                        Email
                    </v-col>
                    <v-col sm="9" class="d-flex">
                        <v-text-field
                            v-model="form.email"
                            class="color-gray d-block inline-edit-input"
                            dark
                            hide-details
                            :disabled="!editingEmail"
                            @blur="endEditing('email')"
                            @keydown="updateProfileHandler($event, 'email')"
                            ref="email"
                        />

                        <v-icon v-if="!editingEmail" @click="startEditing('email')" class="color-gray ml-2">mdi
                            mdi-pencil
                        </v-icon>
                    </v-col>
                </v-row>

                <v-row style="height: 48px;">
                    <v-col sm="3" class="d-flex align-center">
                        User Role
                    </v-col>
                    <v-col sm="9" class="d-flex align-center">
                        {{ account.role_name }}
                    </v-col>
                </v-row>

                <v-row style="height: 48px;">
                    <v-col sm="3" class="d-flex align-center">
                        App Version
                    </v-col>
                    <v-col sm="9" class="d-flex align-center">
                        {{ appVersioo }}
                    </v-col>
                </v-row>

                <div class="d-flex mt-10">
                    <v-dialog
                        v-model="changePasswordDialog"
                        content-class="dashboard-dialog"
                        max-width="450px"
                        transition="fade-transition"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn class="color-gray rounded-xl mr-5" outlined large v-bind="attrs" v-on="on">
                                Change password
                            </v-btn>
                        </template>

                        <v-card>
                            <v-card-title>
                                <span>Change password</span>
                            </v-card-title>
                            <v-card-text class="mt-5">
                                <v-container>
                                    <v-text-field
                                        v-model="form.current_password"
                                        :error-messages="form.$errors.current_password"
                                        background-color="white"
                                        class="dashboard-text-input"
                                        color="#121212"
                                        height="32px"
                                        placeholder="Current password"
                                        type="password"
                                        @blur="form.validate('current_password')"
                                        @input="delete form.$errors.current_password"
                                    />

                                    <v-tooltip v-model="showPasswordTooltip" content-class="password-tooltip--dark"
                                               right
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field
                                                v-model="form.password"
                                                v-bind="attrs"
                                                v-on="on"
                                                :error-messages="form.$errors.password"
                                                background-color="white"
                                                class="dashboard-text-input"
                                                color="#121212"
                                                height="32px"
                                                type="password"
                                                placeholder="New password"
                                                @blur="form.validate('password'); showPasswordTooltip = false;"
                                                @focus="showPasswordTooltip = true"
                                                @input="delete form.$errors.password"
                                            />
                                        </template>
                                        <v-list class="dashboard-tooltip">
                                            <v-list-item>
                                                <v-list-item-title
                                                    :class="{ 'text-decoration-line-through': passValidation.uppercase }"
                                                    class="color-gray"
                                                >One uppercase letter
                                                </v-list-item-title>
                                            </v-list-item>
                                            <v-list-item>
                                                <v-list-item-title
                                                    :class="{ 'text-decoration-line-through': passValidation.lowercase }"
                                                    class="color-gray"
                                                >One lowercase letter
                                                </v-list-item-title>
                                            </v-list-item>
                                            <v-list-item>
                                                <v-list-item-title
                                                    :class="{ 'text-decoration-line-through': passValidation.number }"
                                                    class="color-gray"
                                                >One number
                                                </v-list-item-title>
                                            </v-list-item>
                                            <v-list-item>
                                                <v-list-item-title
                                                    :class="{ 'text-decoration-line-through': passValidation.specialChar }"
                                                    class="color-gray"
                                                >One special character
                                                </v-list-item-title>
                                            </v-list-item>
                                            <v-list-item>
                                                <v-list-item-title
                                                    :class="{ 'text-decoration-line-through': passValidation.length }"
                                                    class="color-gray"
                                                >8+ characters
                                                </v-list-item-title>

                                            </v-list-item>
                                        </v-list>
                                    </v-tooltip>

                                    <v-text-field
                                        v-model="form.password_confirmation"
                                        :error-messages="form.$errors.password_confirmation"
                                        background-color="white"
                                        class="dashboard-text-input"
                                        color="#121212"
                                        height="32px"
                                        type="password"
                                        placeholder="Repeat password"
                                        @blur="form.validate('password_confirmation')"
                                        @input="delete form.$errors.password_confirmation"
                                    />
                                </v-container>
                            </v-card-text>
                            <v-card-actions class="mt-5">
                                <v-btn class="--confirm mr-5" text
                                       @click="updatePasswordHandler"
                                >
                                    Update
                                </v-btn>

                                <v-btn class="--cancel" text
                                       @click="changePasswordDialog = false"
                                >
                                    Cancel
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>

                    <v-dialog
                        v-model="signoutEverywhereDialog"
                        content-class="dashboard-dialog"
                        max-width="450px"
                        transition="fade-transition"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn class="color-gray rounded-xl" outlined large v-bind="attrs" v-on="on">
                                Sign out everywhere
                            </v-btn>
                        </template>

                        <v-card>
                            <v-card-title>
                                <span>Signout everywhere</span>
                            </v-card-title>
                            <v-card-text class="mt-10">
                                <v-container>
                                    <span class="dashboard-text d-block text-center color-gray">Are you sure you want to sign out wherever your fylm.ai account is open?</span>
                                </v-container>
                            </v-card-text>
                            <v-card-actions class="mt-5">
                                <v-btn class="--confirm mr-5" text
                                       @click="signoutEverywhere"
                                >
                                    Confirm
                                </v-btn>

                                <v-btn class="--cancel" text
                                       @click="signoutEverywhereDialog = false"
                                >
                                    Cancel
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </div>
            </div>

            <div class="dashboard-boxed-container">
                <span class="dashboard-subtitle mb-4">Your Organisation</span>
                <v-row class="mb-4 flex-initial">
                    <v-col sm="3" class="d-flex align-center">
                        Organisation
                    </v-col>
                    <v-col sm="9" class="d-flex">
                        <v-text-field
                            v-model="organisation.name"
                            class="color-gray d-block inline-edit-input"
                            dark
                            hide-details
                            :disabled="!editingOrganization"
                            @blur="endEditing('organization')"
                            @keydown="updateOrganisation($event, 'organization')"
                            ref="organization"
                        />

                        <v-icon v-if="canEditOrganization && !editingOrganization" @click="startEditing('organization')"
                                class="color-gray ml-2"
                        >mdi
                            mdi-pencil
                        </v-icon>
                    </v-col>
                </v-row>

                <div class="d-flex mt-auto">
                    <v-btn v-if="canLeaveOrganization" class="btn-danger rounded-xl mr-5" large outlined
                           @click="leaveOrganization()"
                    >
                        Leave Organisation
                    </v-btn>
                </div>

            </div>
        </div>

        <div v-if="canSeePlan" class="dashboard-boxed-container mb-4">
            <span class="dashboard-subtitle mb-4">Your plan</span>

            <v-row class="mb-3">
                <v-col sm="2" class="d-flex align-center">
                    <span>Plan</span>
                </v-col>
                <v-col sm="10" class="d-flex align-center">
                    <span>{{ userPlan }}</span>
                    <span class="color-red ml-1">{{ userPlanState }}</span>
                    <span v-if="currentPlan.fastspring.subscription" class="ml-2">(ID: {{
                            currentPlan.fastspring.subscription
                        }})</span>
                    <i v-if="currentPlan.fastspring.subscription" class="material-icons ml-2"
                       @click="copySubscriptionId" v-tippy="{theme: 'dashboard'}"
                       content="Copy ID" style="font-size: 18px; cursor: pointer;"
                    >content_copy</i>
                </v-col>
            </v-row>

            <v-row v-if="currentPlan && currentPlan.id != 1 && currentPlan.id !== PLAN_LITE_FREE.id && canSeeBillingPage" class="mb-4">
                <v-col sm="2">
                    Next Bill
                </v-col>
                <v-col sm="10">
                    Your next bill is for {{ currentPlan.fastspring.nextChargeTotalDisplay }} on
                    {{ Moment(currentPlan.fastspring.nextChargeDate).format("MMMM Do, YYYY") }}
                </v-col>
            </v-row>

            <v-btn v-if="canSeeBillingPage" class="color-gray rounded-xl mt-10 align-self-start" large outlined
                   @click="goToChangePlanView"
            >
                Change plan
            </v-btn>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import * as rules from "vuelidate/lib/validators";
import CanMixin from "@/mixins/CanMixin";

export default {
    mixins: [CanMixin],

    data() {
        return {
            editingName: false,
            editingEmail: false,
            editingOrganization: false,

            changePasswordDialog: false,
            showPasswordTooltip: false,
            oldEmail: null,

            signoutEverywhereDialog: false,

            organisation: new Form(this, "organisation", {
                name: null,
            }),

            form: new Form(this, "form", {
                name: null,
                email: null,
                current_password: null,
                password: null,
                password_confirmation: null,
            }),
        };
    },

    validations: {
        form: {
            name: {
                required: rules.required,
                minLength: rules.minLength(4),
                fullName: rules.helpers.regex("fullName", /^[a-zA-Z]{1,}( [a-zA-Z]{1,}){1,}$/),
            },
            email: {
                required: rules.required,
                email: rules.email
            },
            current_password: {
                requiredIf: rules.requiredIf(form => {
                    return form.password;
                }),
            },
            password: {
                minLength: rules.minLength(8),
                password: rules.helpers.regex("password", /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[*.!@#$%^&(){}[:;<>,.?~_/+=|]).{8,32}$/),
            },
            password_confirmation: {
                requiredIf: rules.requiredIf(form => {
                    return form.password;
                }),
                sameAs: rules.sameAs("password"),
            },
        },

        orgnisation: {
            name: {
                required: rules.required,
            },
        },
    },

    watch: {
        '$store.state.User.account': {
            immediate: true,
            handler() {
                this.organisation.name = this.account.organization.name;
            }
        },
        '$store.state.User.user': {
            immediate: true,
            handler() {
                this.form.name = this.user.name;
                this.form.email = this.user.email;
                this.oldEmail = this.form.email;
            }
        },

    },

    computed: {
        ...mapState("User", ["account", "currentPlan", "user"]),
        ...mapState("Plans", ['PLAN_LITE_FREE']),
        ...mapState("Organizations", ["pendingUsers"]),
        // ...mapGetters("User", ["user"]),

        appVersioo() {
            return this.APP_VESRION;
        },

        userPlan() {
            if (this.currentPlan?.id == 1 || this.currentPlan?.id == this.PLAN_LITE_FREE.id) return "Free";
            else return this.currentPlan?.fastspring?.display;
        },

        userPlanState() {
            switch (this.currentPlan.fastspring.state) {
                case "canceled":
                    return "[CANCELED]";
                case "overdue":
                    if (this.currentPlan.id !== 1) return "[OVERDUE]";
                    else return null;
                default:
                    return null;
            }
        },

        planQuota() {
            if (this.currentPlan) {
                return (this.currentPlan.plan_quota.used_storage / this.currentPlan.plan_quota.max_storage) * 100;
            }
            return null;
        },

        usersQuota() {
            if (this.currentPlan) {
                if (this.currentPlan.id == 1) return 100;
                return (
                    ((this.currentPlan.users.added + 1 - this.pendingUsers.length) / this.planData('number_of_collaborators')) *
                    100
                );
            }
            return null;
        },

        passValidation() {
            return {
                uppercase: this.form.password ? /[A-Z]/.test(this.form.password) : false,
                lowercase: this.form.password ? /[a-z]/.test(this.form.password) : false,
                number: this.form.password ? /[0-9]/.test(this.form.password) : false,
                specialChar: this.form.password ? /[*.!@#$%^&(){}[:;<>,.?~_/+=|]/.test(this.form.password) : false,
                length: this.form.password ? /^.{8,}$/.test(this.form.password) : false,
            };
        },
    },

    methods: {
        planData(property) {
            let planData = this.$store.getters['Plans/planData'](this.account.plan_id);
            if (planData) return planData[property];
            else return null;
        },

        goToChangePlanView() {
            this.$router.push({ name: "account.billing.changePlan" });
        },

        signoutEverywhere() {
            this.$apiService.get(`accounts/${this.account.id}/users/${this.user.id}/logout`).then(response => {
                this.$router.push({ name: "signout" });
            });
        },

        startEditing(data) {
            if (data == 'name') this.editingName = true;
            if (data == 'email') this.editingEmail = true;
            if (data == 'organization') this.editingOrganization = true;

            this.$nextTick(() => {
                this.$refs[data].$el.querySelector('input').focus();
            })
        },

        endEditing(data) {
            if (data == 'name') this.editingName = false;
            if (data == 'email') this.editingEmail = false;
            if (data == 'organization') this.editingOrganization = false;
        },

        updateProfileHandler(e, data) {
            if (e.key == "Enter" || e.keyCode == 13) {
                this.endEditing(data);
                if (!this.form.validate()) return;
                this.updateProfile();
            } else if (e.key == "Escape" || e.keyCode == 27) {
                this.endEditing(data);
            }
        },

        async updatePasswordHandler() {
            if (!this.form.validate()) return;

            try {
                await this.updateProfile();
                this.changePasswordDialog = false;
                this.$snackbar(`Password updated successfully.`, "success");
            } catch (err) {
                console.log(err);
            }
        },

        async updateProfile() {
            let response = await this.$store.dispatch("User/updateProfile", this.form);
            if (this.oldEmail != this.form.email) this.$router.push({ name: "verify-email" });
            return response;
        },

        updateOrganisation(e, data) {
            if (e.key == "Enter" || e.keyCode == 13) {
                this.endEditing(data);

                if (!this.organisation.validate()) return;

                this.$apiService
                    .patch(`accounts/${this.account.id}/organizations/${this.account.organization.id}`, this.organisation)
                    .then(response => {
                        this.$store.dispatch("User/fetchUser");
                    });
            } else if (e.key == "Escape" || e.keyCode == 27) {
                this.endEditing(data);
            }

        },

        async leaveOrganization() {
            await this.$store.dispatch('Organizations/leave');
            this.$store.dispatch('Auth/unsetActiveAccount');
            await this.$store.dispatch("User/fetchUser");
            this.$router.go();
        },

        copySubscriptionId() {
            navigator.clipboard.writeText(this.currentPlan.fastspring.subscription);
        }
    },
};
</script>

<style lang="scss" scoped>
.password-tooltip {
    background-color: white;
    box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.2), 0 5px 8px 0 rgba(0, 0, 0, 0.14), 0 1px 14px 0 rgba(0, 0, 0, 0.12);
}
</style>
